import { ReferralState } from '../__generated__/bffGlobalTypes';
import type { SearchReferrals } from '../graphql/queries/__generated__/SearchReferrals';

type SearchReferralsReferral = SearchReferrals['searchReferrals']['referrals'][number];
type LeadOffer = NonNullable<NonNullable<SearchReferralsReferral['lead']>['offer']>;

/**
 * NHC-facing milestones. The `ReferralState` enum is too granular for a
 * birds-eye view — an NHC looking at 30 customers cares "where is each one in the
 * funnel," not the exact sub-state. Milestones bucket ReferralState into six
 * NHC-relevant stages, with `ATTENTION` reserved for anything that needs the NHC's
 * intervention (denied, expired, withdrawn, stuck flow).
 */
export enum Milestone {
  ATTENTION = 'ATTENTION',
  REQUESTED = 'REQUESTED',
  OFFER_SENT = 'OFFER_SENT',
  IN_ASSESSMENTS = 'IN_ASSESSMENTS',
  IN_CONTRACT = 'IN_CONTRACT',
  CLOSED = 'CLOSED',
}

/** Order in which milestone groups render in the birds-eye list. */
export const MILESTONE_ORDER = [
  Milestone.ATTENTION,
  Milestone.IN_CONTRACT,
  Milestone.IN_ASSESSMENTS,
  Milestone.OFFER_SENT,
  Milestone.REQUESTED,
  Milestone.CLOSED,
] as const satisfies readonly Milestone[];

// Compile-time guard: adding a new Milestone without extending MILESTONE_ORDER
// makes this alias non-`never`, which fails the assertion below.
type _MissingFromMilestoneOrder = Exclude<Milestone, (typeof MILESTONE_ORDER)[number]>;
const _milestoneOrderIsExhaustive: _MissingFromMilestoneOrder extends never ? true : false = true;
void _milestoneOrderIsExhaustive;

export const MILESTONE_LABEL: Record<Milestone, string> = {
  [Milestone.ATTENTION]: 'Needs attention',
  [Milestone.REQUESTED]: 'Offer requested',
  [Milestone.OFFER_SENT]: 'Offer sent',
  [Milestone.IN_ASSESSMENTS]: 'In assessments',
  [Milestone.IN_CONTRACT]: 'In contract',
  [Milestone.CLOSED]: 'Closed',
};

/** Fine-grained status label per ReferralState, shown as sub-text under the milestone. */
const SUB_STATUS_LABEL: Partial<Record<ReferralState, string>> = {
  [ReferralState.UNKNOWN]: 'Unknown state',
  [ReferralState.LEAD_DENIED]: 'Denied',
  [ReferralState.LEAD_QUALIFIED]: 'Offer requested',
  [ReferralState.LEAD_SELLER_FLOW_INCOMPLETE]: 'Seller flow incomplete',
  [ReferralState.OFFER_FINAL_OFFER_UNRELEASED]: 'Final offer pending',
  [ReferralState.OFFER_SENT]: 'Offer sent',
  [ReferralState.OFFER_EXPIRED]: 'Offer expired',
  [ReferralState.OFFER_REJECTED]: 'Offer rejected',
  [ReferralState.OFFER_WITHDRAWN]: 'Withdrawn',
  [ReferralState.OFFER_ACCEPTED]: 'Offer accepted',
  [ReferralState.OFFER_ASSESSMENTS_COMPLETED]: 'Assessments completed',
  [ReferralState.OFFER_IN_CONTRACT]: 'Contract signed',
  [ReferralState.OFFER_CONTINGENCIES_RELEASED]: 'Contingencies released',
  [ReferralState.OFFER_CLOSED]: 'Closed',
};

export type CustomerRowData = {
  referralUuid: string;
  fullName: string | null;
  street: string;
  email: string | null;
  phoneNumber: string | null;
  milestone: Milestone;
  subStatus: string;
  lastUpdate: Date | null;
};

/** Buckets a ReferralState into the corresponding NHC-facing milestone. */
function milestoneFor(state: ReferralState): Milestone {
  switch (state) {
    case ReferralState.LEAD_DENIED:
    case ReferralState.LEAD_SELLER_FLOW_INCOMPLETE:
    case ReferralState.OFFER_EXPIRED:
    case ReferralState.OFFER_REJECTED:
    case ReferralState.OFFER_WITHDRAWN:
    case ReferralState.UNKNOWN:
      return Milestone.ATTENTION;
    case ReferralState.LEAD_QUALIFIED:
      return Milestone.REQUESTED;
    case ReferralState.OFFER_SENT:
    case ReferralState.OFFER_FINAL_OFFER_UNRELEASED:
      return Milestone.OFFER_SENT;
    case ReferralState.OFFER_ACCEPTED:
    case ReferralState.OFFER_ASSESSMENTS_COMPLETED:
      return Milestone.IN_ASSESSMENTS;
    case ReferralState.OFFER_IN_CONTRACT:
    case ReferralState.OFFER_CONTINGENCIES_RELEASED:
      return Milestone.IN_CONTRACT;
    case ReferralState.OFFER_CLOSED:
      return Milestone.CLOSED;
    default:
      return Milestone.ATTENTION;
  }
}

/**
 * Picks the "most representative" timestamp per milestone — used to sort
 * within a bucket (most recent first). Falls back to qualifiedAt.
 */
function lastUpdateFor(
  milestone: Milestone,
  offer: LeadOffer | null,
  qualifiedAt: string | null,
): Date | null {
  const pick = (raw: string | null | undefined): Date | null => {
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  switch (milestone) {
    case Milestone.CLOSED:
      return pick(offer?.closeEscrowDate) ?? pick(qualifiedAt);
    case Milestone.IN_CONTRACT:
    case Milestone.IN_ASSESSMENTS:
      return pick(offer?.acceptedAt) ?? pick(qualifiedAt);
    case Milestone.ATTENTION:
      return (
        pick(offer?.withdrawnAt) ??
        pick(offer?.expiredAt) ??
        pick(offer?.acceptedAt) ??
        pick(qualifiedAt)
      );
    case Milestone.OFFER_SENT:
    case Milestone.REQUESTED:
      return pick(qualifiedAt);
  }
}

/** Days between a past date and now. Returns Infinity if `date` is null/invalid. */
function daysSince(date: Date | null): number {
  if (!date) return Infinity;
  return (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
}

/**
 * Preserves the historical hide rules from the old CustomerCard view: don't
 * surface stale requests (offer never delivered within 14d), broken offers
 * missing all expiry timestamps, or closings older than 30 days. Keeps the
 * new list from bloating with dead referrals.
 */
function shouldHide(referral: SearchReferralsReferral, milestone: Milestone): boolean {
  const lead = referral.lead;
  if (!lead) return true;

  if (milestone === Milestone.REQUESTED) {
    return daysSince(lead.qualifiedAt ? new Date(lead.qualifiedAt) : null) >= 14;
  }

  if (milestone === Milestone.CLOSED) {
    // Match the old CustomerCard behavior: keep OFFER_CLOSED visible when the
    // close date is missing (rare data state), and only hide when we know the
    // close was more than 30 days ago.
    const closeDate = lead.offer?.closeEscrowDate ? new Date(lead.offer.closeEscrowDate) : null;
    return closeDate !== null && daysSince(closeDate) >= 30;
  }

  return false;
}

export function getCustomerRowData(referral: SearchReferralsReferral): CustomerRowData | null {
  const { lead, uuid } = referral;
  if (!lead) return null;

  const milestone = milestoneFor(referral.state);
  if (shouldHide(referral, milestone)) return null;

  return {
    referralUuid: uuid,
    fullName: lead.initialCustomer?.fullName ?? lead.fullName ?? null,
    street: lead.address.street,
    email: lead.initialCustomer?.email ?? lead.email ?? null,
    phoneNumber: lead.phoneNumber ?? null,
    milestone,
    subStatus: SUB_STATUS_LABEL[referral.state] ?? 'Unknown state',
    lastUpdate: lastUpdateFor(milestone, lead.offer ?? null, lead.qualifiedAt ?? null),
  };
}

/** Groups rows into MILESTONE_ORDER buckets, sorted within each bucket by lastUpdate desc. */
export function groupByMilestone(
  rows: readonly CustomerRowData[],
): Array<{ milestone: Milestone; rows: CustomerRowData[] }> {
  const buckets = new Map<Milestone, CustomerRowData[]>();
  for (const m of MILESTONE_ORDER) buckets.set(m, []);
  for (const row of rows) buckets.get(row.milestone)!.push(row);

  for (const bucket of buckets.values()) {
    bucket.sort((a, b) => (b.lastUpdate?.getTime() ?? 0) - (a.lastUpdate?.getTime() ?? 0));
  }

  return MILESTONE_ORDER.filter((m) => buckets.get(m)!.length > 0).map((m) => ({
    milestone: m,
    rows: buckets.get(m)!,
  }));
}
